import { supabase } from '@/integrations/supabase/client';
import { Session } from '@/types/session';
import { SessionError, withRetry, createErrorContext } from '@/lib/errorHandling';
import { crossTabSync } from '@/lib/crossTabSync';
import { toast } from '@/hooks/use-toast';
import { calculateRemainingSeconds } from '@/utils/sessionTimeUtils';

let hasWarnedInvalidFunctionsUrl = false;
let hasWarnedFunctionsUrlFallback = false;

function getResolvedFunctionsUrl(): string | null {
  const configuredFunctionsUrl = (import.meta.env.VITE_SUPABASE_FUNCTIONS_URL as string | undefined)?.trim();
  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim();
  const normalizedConfiguredUrl = configuredFunctionsUrl ? configuredFunctionsUrl.replace(/\/$/, '') : null;
  const derivedFunctionsUrl = supabaseUrl ? `${supabaseUrl.replace(/\/$/, '')}/functions/v1` : null;

  let resolvedUrl = normalizedConfiguredUrl ?? derivedFunctionsUrl;
  if (!resolvedUrl) {
    return null;
  }

  let parsedResolvedUrl: URL | null = null;
  try {
    parsedResolvedUrl = new URL(resolvedUrl);
  } catch {
    if (derivedFunctionsUrl && resolvedUrl !== derivedFunctionsUrl) {
      resolvedUrl = derivedFunctionsUrl;
      try {
        parsedResolvedUrl = new URL(resolvedUrl);
      } catch {
        parsedResolvedUrl = null;
      }
    }
  }

  if (!parsedResolvedUrl) {
    if (!hasWarnedInvalidFunctionsUrl) {
      console.warn('Supabase functions URL is invalid; session edge-function calls will be skipped.');
      hasWarnedInvalidFunctionsUrl = true;
    }
    return null;
  }

  if (normalizedConfiguredUrl && derivedFunctionsUrl) {
    let parsedConfiguredUrl: URL | null = null;
    let parsedDerivedUrl: URL | null = null;
    try {
      parsedConfiguredUrl = new URL(normalizedConfiguredUrl);
      parsedDerivedUrl = new URL(derivedFunctionsUrl);
    } catch {
      // Ignore parse errors; handled by resolved URL validation above.
    }

    const configuredIsLocal = parsedConfiguredUrl
      ? /^(localhost|127\.0\.0\.1)$/i.test(parsedConfiguredUrl.hostname)
      : false;
    const derivedIsRemote = parsedDerivedUrl
      ? !/^(localhost|127\.0\.0\.1)$/i.test(parsedDerivedUrl.hostname)
      : false;

    if (configuredIsLocal && derivedIsRemote) {
      resolvedUrl = derivedFunctionsUrl;
      parsedResolvedUrl = parsedDerivedUrl ?? parsedResolvedUrl;
      if (!hasWarnedFunctionsUrlFallback) {
        console.warn(
          `VITE_SUPABASE_FUNCTIONS_URL points to localhost (${normalizedConfiguredUrl}) while VITE_SUPABASE_URL is remote (${supabaseUrl}); using derived functions URL ${resolvedUrl}.`
        );
        hasWarnedFunctionsUrlFallback = true;
      }
    }
  }

  const isLocalFunctionsUrl = /^(localhost|127\.0\.0\.1)$/i.test(parsedResolvedUrl.hostname);
  const runtimeHostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown-host';
  const isLocalRuntime =
    /^(localhost|127\.0\.0\.1)$/i.test(runtimeHostname);

  if (isLocalFunctionsUrl && !isLocalRuntime) {
    if (!hasWarnedInvalidFunctionsUrl) {
      console.warn(
        `Supabase functions URL points to localhost (${resolvedUrl}) while app is running on ${runtimeHostname}; session edge-function calls will be skipped.`
      );
      hasWarnedInvalidFunctionsUrl = true;
    }
    return null;
  }

  return resolvedUrl;
}

/**
 * SessionStateManager - Database-first session state management service
 * 
 * This service establishes the database as the single source of truth for session existence
 * while providing localStorage fallback for offline mode and UI optimization.
 * 
 * Key principles:
 * - Database always takes precedence over localStorage for session existence
 * - localStorage only used for smooth UI updates and offline mode
 * - Conflict resolution when database and localStorage disagree
 * - Operation queuing for network recovery
 */
interface QueuedUpdate {
  id: string;
  sessionId: string;
  status: string;
  userId: string;
  completedAt?: string;
  focusScore?: number;
  attempts: number;
  timestamp: number;
  lastAttempt?: number;
}

class SessionStateManager {
  private static instance: SessionStateManager | null = null;
  private static QUEUE_KEY = 'session_update_queue';
  private operationQueue: Array<() => Promise<void>> = [];
  private isOnline: boolean = true;
  private retryTimeout: NodeJS.Timeout | null = null;
  private maxRetries: number = 3;
  private baseRetryDelay: number = 5000; // 5 seconds base delay

  private constructor() {
    // Set up online/offline detection
    this.setupNetworkDetection();
    // Load persisted queue from localStorage on initialization
    this.loadQueueFromStorage();
    // Process any queued operations if online
    if (this.isOnline) {
      setTimeout(() => this.processOperationQueue(), 1000);
    }
  }

  public static getInstance(): SessionStateManager {
    if (!SessionStateManager.instance) {
      SessionStateManager.instance = new SessionStateManager();
    }
    return SessionStateManager.instance;
  }

  /**
   * Fetch active session from database (primary source of truth)
   * Falls back to localStorage validation if database is unavailable
   */
  public async fetchActiveSession(userId: string): Promise<Session | null> {
    const context = createErrorContext('fetchActiveSession', 'SessionStateManager');
    
    try {
      // Always try database first
      const session = await this.fetchFromDatabase(userId);
      
      if (session) {
        // Validate localStorage against database session
        await this.validateLocalStorageState(session);
        return session;
      }
      
      // No database session found - clear any orphaned localStorage
      this.clearOrphanedLocalStorage();
      return null;
      
    } catch (error) {
      console.warn('Database fetch failed, attempting localStorage fallback:', error);
      
      // Database failed - try localStorage fallback for offline mode
      return await this.fetchFromLocalStorage(userId);
    }
  }

  /**
   * Fetch session from database (primary source)
   */
  private async fetchFromDatabase(userId: string): Promise<Session | null> {
    const context = createErrorContext('fetchFromDatabase', 'SessionStateManager');
    
    const fetchWithRetry = withRetry(
      async () => {
        const { data, error } = await supabase
          .from('sessions')
          .select('*')
          .eq('user_id', userId)
          .or('status.eq.active,status.eq.paused')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
          throw new SessionError(
            `Database fetch failed: ${error.message}`,
            'DATABASE_FETCH_ERROR',
            context
          );
        }

        return data;
      },
      3, // maxRetries
      1000, // delay
      'SessionStateManager.fetchFromDatabase'
    );

    const data = await fetchWithRetry();
    
    if (!data) {
      return null;
    }

    // Validate session hasn't expired
    if (data.started_at && data.status === 'active') {
      const startTime = new Date(data.started_at).getTime();
      const now = Date.now();
      const totalSeconds = data.duration * 60;
      const elapsed = Math.floor((now - startTime) / 1000);
      
      if (elapsed >= totalSeconds) {
        // Session has expired - mark as completed
        await this.completeExpiredSession(data.id);
        return null;
      }
    }

    return this.mapDatabaseSession(data);
  }

  /**
   * Fetch session from localStorage (fallback for offline mode)
   */
  private async fetchFromLocalStorage(userId: string): Promise<Session | null> {
    try {
      const timerState = localStorage.getItem('deepfocus_timer_state');
      if (!timerState) {
        return null;
      }

      const parsed = JSON.parse(timerState);
      
      // Validate localStorage has required fields
      if (!parsed.sessionId || !parsed.totalDuration) {
        console.warn('Invalid localStorage timer state, clearing');
        localStorage.removeItem('deepfocus_timer_state');
        return null;
      }

      // Create a minimal session object from localStorage
      // This is only used for offline mode when database is unavailable
      const session: Session = {
        id: parsed.sessionId,
        user_id: userId,
        goal: parsed.goal || 'Focus Session',
        duration: Math.ceil(parsed.totalDuration / 60), // Convert seconds to minutes
        status: parsed.isPaused ? 'paused' : 'active',
        invite_code: null,
        started_at: parsed.startTime ? new Date(parsed.startTime).toISOString() : null,
        completed_at: null,
        paused_at: parsed.isPaused ? new Date().toISOString() : null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      console.log('Using localStorage fallback session:', session);
      return session;
      
    } catch (error) {
      console.error('Failed to parse localStorage timer state:', error);
      localStorage.removeItem('deepfocus_timer_state');
      return null;
    }
  }

  /**
   * Validate localStorage state against database session
   */
  private async validateLocalStorageState(databaseSession: Session): Promise<void> {
    try {
      const timerState = localStorage.getItem('deepfocus_timer_state');
      if (!timerState) {
        return; // No localStorage state to validate
      }

      const parsed = JSON.parse(timerState);
      
      // Check if localStorage session ID matches database session ID
      if (parsed.sessionId !== databaseSession.id) {
        console.warn('Session ID mismatch between database and localStorage, clearing localStorage');
        localStorage.removeItem('deepfocus_timer_state');
        return;
      }

      // Validate other critical fields match
      const expectedDuration = databaseSession.duration * 60; // Convert minutes to seconds
      if (Math.abs(parsed.totalDuration - expectedDuration) > 60) { // Allow 1 minute tolerance
        console.warn('Duration mismatch between database and localStorage, updating localStorage');
        parsed.totalDuration = expectedDuration;
      }

      // Timestamp consistency (database is source of truth)
      const startedAtMs = databaseSession.started_at ? new Date(databaseSession.started_at).getTime() : null;
      const now = Date.now();

      if (startedAtMs && Number.isFinite(startedAtMs) && startedAtMs > now + 30_000) {
        console.warn('Invalid database started_at (in the future); clearing localStorage timer state');
        localStorage.removeItem('deepfocus_timer_state');
        return;
      }

      if (startedAtMs && parsed.startTime && Math.abs(parsed.startTime - startedAtMs) > 30_000) {
        console.warn('Start time mismatch between database and localStorage, updating localStorage');
        parsed.startTime = startedAtMs;
      }

      // Validate that remaining time matches timestamp calculation (within tolerance)
      const expectedRemaining = calculateRemainingSeconds(databaseSession);
      const parsedRemaining: number | null =
        typeof parsed.remainingTime === 'number'
          ? parsed.remainingTime
          : typeof parsed.elapsedTime === 'number' && typeof parsed.totalDuration === 'number'
            ? Math.max(0, parsed.totalDuration - parsed.elapsedTime)
            : null;

      if (parsedRemaining !== null && Math.abs(parsedRemaining - expectedRemaining) > 5) {
        console.warn('Remaining time mismatch between database and localStorage, updating localStorage');
        parsed.remainingTime = expectedRemaining;
        parsed.elapsedTime = Math.max(0, expectedDuration - expectedRemaining);
        parsed.progress = expectedDuration > 0 ? Math.min(100, (parsed.elapsedTime / expectedDuration) * 100) : 0;
      }

      // Ensure paused/running flags align with DB status
      parsed.isPaused = databaseSession.status === 'paused';
      parsed.isRunning = databaseSession.status === 'active';

      localStorage.setItem('deepfocus_timer_state', JSON.stringify(parsed));

    } catch (error) {
      console.error('Failed to validate localStorage state:', error);
      localStorage.removeItem('deepfocus_timer_state');
    }
  }

  /**
   * Clear orphaned localStorage state when no database session exists
   */
  private clearOrphanedLocalStorage(): void {
    const timerState = localStorage.getItem('deepfocus_timer_state');
    if (timerState) {
      console.log('Clearing orphaned localStorage timer state');
      localStorage.removeItem('deepfocus_timer_state');
    }
  }

  /**
   * Complete an expired session in the database
   */
  private async completeExpiredSession(sessionId: string): Promise<void> {
    const context = createErrorContext('completeExpiredSession', 'SessionStateManager');
    
    const updateWithRetry = withRetry(
      async () => {
        const { error } = await supabase
          .from('sessions')
          .update({
            status: 'completed',
            completed_at: new Date().toISOString()
          })
          .eq('id', sessionId);

        if (error) {
          throw new SessionError(
            `Failed to complete expired session: ${error.message}`,
            'SESSION_COMPLETION_ERROR',
            context
          );
        }
      },
      3,
      1000,
      'SessionStateManager.completeExpiredSession'
    );

    await updateWithRetry();
  }

  /**
   * Map database session data to Session interface
   */
  private mapDatabaseSession(data: any): Session {
    return {
      id: data.id,
      user_id: data.user_id,
      goal: data.goal,
      duration: data.duration,
      type: data.type,
      status: data.status,
      invite_code: data.invite_code,
      started_at: data.started_at,
      completed_at: data.completed_at,
      paused_at: data.paused_at,
      created_at: data.created_at,
      updated_at: data.updated_at,
      focus_score: data.focus_score,
      sync_ratio: data.sync_ratio,
      active_time: data.active_time,
      interruptions: data.interruptions,
      participant_limit: data.participant_limit,
      event_room: data.event_room,
    };
  }

  /**
   * Update session status in database
   */
  public async updateSessionStatus(sessionId: string, status: string, userId: string): Promise<void> {
    const context = createErrorContext('updateSessionStatus', 'SessionStateManager');
    
    const updateData: Record<string, unknown> = { status };
    
    if (status === 'paused') {
      updateData.paused_at = new Date().toISOString();
    } else if (status === 'active') {
      updateData.paused_at = null;
    } else if (status === 'completed' || status === 'cancelled') {
      updateData.completed_at = new Date().toISOString();
    }

    const updateWithRetry = withRetry(
      async () => {
        const functionsUrl = getResolvedFunctionsUrl();
        if (!functionsUrl) {
          throw new SessionError(
            'Supabase functions URL is not available for this runtime',
            'CONFIGURATION_ERROR',
            context
          );
        }

        try {
          const response = await fetch(`${functionsUrl}/session-management/status`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
            },
            body: JSON.stringify({
              session_id: sessionId,
              ...updateData
            })
          });

          if (!response.ok) {
            // Check if it's a CORS error (response.ok is false but we can't read body due to CORS)
            if (response.status === 0 || response.type === 'opaque') {
              throw new SessionError(
                'CORS error: Edge function does not allow PATCH requests from this origin',
                'CORS_ERROR',
                context
              );
            }
            
            let errorData;
            try {
              errorData = await response.json();
            } catch {
              // If we can't parse JSON, it might be a CORS issue
              throw new SessionError(
                `Session update failed: HTTP ${response.status} - Possible CORS issue`,
                'CORS_ERROR',
                context
              );
            }
            
            throw new SessionError(
              `Session update failed: ${errorData.error || 'Unknown error'}`,
              'SESSION_UPDATE_ERROR',
              context
            );
          }
        } catch (fetchError) {
          // Check if it's a CORS error from the browser
          // CORS errors throw TypeError with "Failed to fetch" message
          // Also check if the error message contains CORS-related text
          const isCorsError = fetchError instanceof TypeError && 
            (fetchError.message === 'Failed to fetch' || 
             fetchError.message.includes('CORS') ||
             fetchError.message.includes('Access-Control'));
          
          if (isCorsError) {
            throw new SessionError(
              'CORS error: Edge function does not allow PATCH requests from this origin',
              'CORS_ERROR',
              context
            );
          }
          // Re-throw other errors
          throw fetchError;
        }
      },
      1, // Only try once - if CORS fails, retry won't help
      1000,
      'SessionStateManager.updateSessionStatus'
    );

    try {
      // withRetry now throws on error, so if we get here, it succeeded
      await updateWithRetry();
      
      console.log('✅ Session status updated successfully:', sessionId, status);
      
      // Broadcast session update to other tabs
      crossTabSync.broadcast({
        type: 'session_updated',
        sessionId,
        userId,
        data: { status, timestamp: new Date().toISOString() }
      });
    } catch (error) {
      // Check if it's a CORS error - don't queue for retry (CORS won't fix itself)
      const isCorsError = error instanceof SessionError && error.code === 'CORS_ERROR';
      
      if (isCorsError) {
        // For CORS errors, don't queue - just throw so caller can fall back to direct DB update
        // Don't trigger error handling toasts since we have a fallback
        throw error;
      }
      
      // For other errors, queue operation for retry when connection is restored
      this.queueUpdateForRetry(sessionId, status, userId);
      
      // Show user feedback
      if (!this.isOnline) {
        toast({
          title: "Offline",
          description: "Update will sync when connection is restored",
          duration: 3000,
        });
      }
      
      throw error;
    }
  }

  /**
   * Queue update for retry with localStorage persistence
   */
  private queueUpdateForRetry(sessionId: string, status: string, userId: string): void {
    const update: QueuedUpdate = {
      id: `${sessionId}-${Date.now()}`,
      sessionId,
      status,
      userId,
      attempts: 0,
      timestamp: Date.now(),
    };
    
    const queue = this.getQueuedUpdates();
    queue.push(update);
    this.saveQueueToStorage(queue);
    
    // Also add to in-memory queue for immediate retry
    this.operationQueue.push(() => this.updateSessionStatus(sessionId, status, userId));
    
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
    
    // Retry with exponential backoff
    const delay = this.calculateRetryDelay(update.attempts);
    this.retryTimeout = setTimeout(() => {
      this.processOperationQueue();
    }, delay);
  }

  /**
   * Queue operation for retry when connection is restored (legacy method, kept for compatibility)
   */
  private queueOperation(operation: () => Promise<void>): void {
    this.operationQueue.push(operation);
    
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
    
    this.retryTimeout = setTimeout(() => {
      this.processOperationQueue();
    }, 5000); // Retry after 5 seconds
  }

  /**
   * Process queued operations when connection is restored
   */
  public async processOperationQueue(): Promise<void> {
    // Process both in-memory queue and localStorage queue
    const persistedQueue = this.getQueuedUpdates();
    const hasInMemoryOps = this.operationQueue.length > 0;
    const hasPersistedOps = persistedQueue.length > 0;
    
    if (!hasInMemoryOps && !hasPersistedOps) {
      return;
    }

    console.log(`📡 Processing queued operations: ${this.operationQueue.length} in-memory, ${persistedQueue.length} persisted`);

    // Process in-memory operations first
    const operations = [...this.operationQueue];
    this.operationQueue = [];

    for (const operation of operations) {
      try {
        await operation();
      } catch (error) {
        console.error('Failed to process queued operation:', error);
        // Re-queue failed operations
        this.operationQueue.push(operation);
      }
    }

    // Process persisted queue with attempt tracking
    const remainingUpdates: QueuedUpdate[] = [];
    for (const update of persistedQueue) {
      try {
        update.attempts += 1;
        update.lastAttempt = Date.now();
        
        console.log(`🔄 Retrying queued update (attempt ${update.attempts}/${this.maxRetries}):`, update.sessionId);
        
        await this.updateSessionStatus(update.sessionId, update.status, update.userId);
        
        // Success - remove from queue
        console.log('✅ Successfully processed queued update:', update.sessionId);
        toast({
          title: "Synced",
          description: "Pending update has been synced",
          duration: 2000,
        });
      } catch (error) {
        console.error('❌ Failed to process queued update:', error);
        
        if (update.attempts >= this.maxRetries) {
          // Max retries reached - show error toast with manual retry
          console.error(`❌ Max retries (${this.maxRetries}) reached for update:`, update.sessionId);
          toast({
            title: "Sync Failed",
            description: "Unable to sync session update. Please try again.",
            variant: "destructive",
            action: {
              label: "Retry",
              onClick: () => {
                update.attempts = 0; // Reset attempts for manual retry
                this.queueUpdateForRetry(update.sessionId, update.status, update.userId);
              }
            },
            duration: 5000,
          });
          // Remove from queue after max retries
        } else {
          // Re-queue for retry with exponential backoff
          remainingUpdates.push(update);
        }
      }
    }

    // Save remaining updates back to localStorage
    if (remainingUpdates.length > 0) {
      this.saveQueueToStorage(remainingUpdates);
      // Schedule next retry with exponential backoff
      const nextUpdate = remainingUpdates[0];
      const delay = this.calculateRetryDelay(nextUpdate.attempts);
      this.retryTimeout = setTimeout(() => {
        this.processOperationQueue();
      }, delay);
    } else {
      // All updates processed - clear localStorage queue
      this.clearQueueFromStorage();
    }

    // If there are still failed in-memory operations, retry again later
    if (this.operationQueue.length > 0) {
      this.retryTimeout = setTimeout(() => {
        this.processOperationQueue();
      }, 10000); // Retry after 10 seconds
    }
  }

  /**
   * Set up network detection for online/offline mode
   */
  private setupNetworkDetection(): void {
    const updateOnlineStatus = () => {
      const wasOffline = !this.isOnline;
      this.isOnline = navigator.onLine;
      
      if (this.isOnline && wasOffline) {
        console.log('📡 Connection restored, processing queued operations');
        const persistedQueue = this.getQueuedUpdates();
        if (this.operationQueue.length > 0 || persistedQueue.length > 0) {
          toast({
            title: "Connection Restored",
            description: "Syncing pending updates...",
            duration: 2000,
          });
          this.processOperationQueue();
        }
      } else if (!this.isOnline) {
        console.log('📡 Connection lost, updates will be queued');
      }
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    
    // Initial status
    this.isOnline = navigator.onLine;
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  private calculateRetryDelay(attempts: number): number {
    // Exponential backoff: 5s, 10s, 20s, 40s...
    return Math.min(this.baseRetryDelay * Math.pow(2, attempts), 60000); // Max 60 seconds
  }

  /**
   * Get queued updates from localStorage
   */
  private getQueuedUpdates(): QueuedUpdate[] {
    try {
      const stored = localStorage.getItem(SessionStateManager.QUEUE_KEY);
      if (!stored) return [];
      return JSON.parse(stored);
    } catch (error) {
      console.error('Failed to load queue from localStorage:', error);
      return [];
    }
  }

  /**
   * Save queued updates to localStorage
   */
  private saveQueueToStorage(queue: QueuedUpdate[]): void {
    try {
      localStorage.setItem(SessionStateManager.QUEUE_KEY, JSON.stringify(queue));
      console.log(`💾 Saved ${queue.length} queued updates to localStorage`);
    } catch (error) {
      console.error('Failed to save queue to localStorage:', error);
    }
  }

  /**
   * Load queue from localStorage on initialization
   */
  private loadQueueFromStorage(): void {
    const queue = this.getQueuedUpdates();
    if (queue.length > 0) {
      console.log(`📦 Loaded ${queue.length} queued updates from localStorage`);
    }
  }

  /**
   * Clear queue from localStorage
   */
  private clearQueueFromStorage(): void {
    try {
      localStorage.removeItem(SessionStateManager.QUEUE_KEY);
      console.log('🧹 Cleared queue from localStorage');
    } catch (error) {
      console.error('Failed to clear queue from localStorage:', error);
    }
  }

  /**
   * Get current online status
   */
  public isConnectionOnline(): boolean {
    return this.isOnline;
  }

  /**
   * Get number of queued operations (in-memory + persisted)
   */
  public getQueuedOperationsCount(): number {
    return this.operationQueue.length + this.getQueuedUpdates().length;
  }

  /**
   * Clear all queued operations (in-memory + persisted)
   */
  public clearOperationQueue(): void {
    this.operationQueue = [];
    this.clearQueueFromStorage();
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
  }
}

// Export singleton instance
export const sessionStateManager = SessionStateManager.getInstance();
export default sessionStateManager;

